import { Amplify, API, Auth, Hub } from "aws-amplify";
import { Ref, ref } from "vue";
import { ProfileInfo } from "@/auth/profile-info";
import router from "@/router";
import { usePayee } from "@/payee.composable";
import {
  loadingController,
  modalController,
  toastController,
} from "@ionic/vue";
import { AccountInfo } from "@/auth/account-info";
import { usePaymentMethods } from "@/payment-methods/payment-methods.composable";
import MfaEnrollmentModal from "@/auth/MfaEnrollmentModal.vue";
import MfaCodeModal from "@/auth/MfaCodeModal.vue";

const profileInfo = ref<ProfileInfo>();
const isAuthenticationLoaded = ref(false);

let authResolve: (value?: unknown) => void;
const authLoadingPromise = new Promise((resolve) => (authResolve = resolve));

export function useAuth() {
  return {
    login,
    logout,
    isAuthenticated,
    isAuthenticationLoaded,
    configureAuth,
    profileInfo,
    createAccount,
    authLoadingPromise,
    forgotPassword,
    forgotPasswordSubmit,
    handleCrossPayeeLogout,
  };
}

const isAuthenticated = ref(false);
const { payee } = usePayee();

function handleCrossPayeeLogout() {
  if (
    profileInfo.value &&
    payee.value &&
    !profileInfo.value?.isBrandAdmin &&
    !profileInfo.value?.isGlobalAdmin &&
    !profileInfo.value?.isPayeeAdmin &&
    payee.value?.id !== profileInfo.value?.payeeId
  ) {
    logout("This user is not associated with this payee.");
  }
}

function configureAuth() {
  Amplify.configure({
    Auth: {
      storage: sessionStorage,
      mandatorySignIn: true,
      region: process.env.VUE_APP_AUTH_REGION,
      userPoolId: process.env.VUE_APP_AUTH_USER_POOL_ID,
      userPoolWebClientId: process.env.VUE_APP_AUTH_SERVER_CLIENT_ID,
      oauth: {
        domain: process.env.VUE_APP_AUTH_SERVER_HOST,
        scope: ["email", "openid", "phone"],
        redirectSignIn: `${process.env.VUE_APP_URL}/loginredirect`,
        redirectSignOut: `${process.env.VUE_APP_URL}/loggedout`,
        responseType: "code",
        options: {
          AdvancedSecurityDataCollectionFlag: false,
        },
      },
    },
    API: {
      endpoints: [
        {
          name: "API",
          endpoint: process.env.VUE_APP_API_SERVER,
          custom_header: async () => {
            try {
              const authToken = `Bearer ${(await Auth.currentSession())
                .getAccessToken()
                .getJwtToken()}`;
              return { Authorization: authToken };
            } catch (error) {
              isAuthenticated.value = false;
              return {};
            }
          },
        },
      ],
    },
  });

  const updateAuthStateOnPageLoad = async () => {
    try {
      await Auth.currentSession();
      await fetchUserDataOnLogin();
      isAuthenticated.value = true;
    } catch (error) {
      isAuthenticated.value = false;
    } finally {
      isAuthenticationLoaded.value = true;
      authResolve();
    }
  };

  updateAuthStateOnPageLoad();

  Hub.listen("auth", (data) => {
    const { event } = data.payload;
    if (event === "signIn") {
      isAuthenticated.value = true;
      fetchUserDataOnLogin();
    } else if (event === "signOut" || event === "signIn_failure") {
      isAuthenticated.value = false;
      router.replace(`/${payee.value?.id ?? "admin"}`);
    }
  });
}

async function fetchUserDataOnLogin() {
  try {
    if (!profileInfo.value) {
      profileInfo.value = await getMe();
    }
    if (!profileInfo.value?.isBrandAdmin && !profileInfo.value?.isPayeeAdmin) {
      const { populatePaymentMethods } = usePaymentMethods();
      await populatePaymentMethods();
    }
  } catch (error) {
    console.log(error);
  }
}

async function getMe() {
  return API.get("API", "/v1/me", {});
}

async function login(username: string, password: string) {
  const user = await Auth.signIn(username, password);

  const loading = await loadingController.create({ message: "Verifying..." });

  // Added condition to properly handle what code is executed - Addendum to DEVSUPPORT-286
  if (user.challengeName === "SMS_MFA") {
    try {
      const code = await getTextCode(
        user.challengeParam.CODE_DELIVERY_DESTINATION
      );
      loading.present();
      await Auth.confirmSignIn(user, code, user.challengeName);
    } catch (error) {
      loading.dismiss();
      throw new Error("Invalid Code");
    }

    profileInfo.value = await getMe();

    loading.dismiss();

    if (!profileInfo.value) {
      console.error("Profile info failed to load.");
      logout("Authentication failure. Please try again.");
      return;
    }

    const displayMfaEnrollment =
      payee.value?.isMfaEnabled &&
      !user.challengeName &&
      !profileInfo.value?.isBrandAdmin &&
      !profileInfo.value?.isGlobalAdmin;
    if (displayMfaEnrollment) {
      await getPhoneNumber(user.username);
    }
  } else {
    profileInfo.value = await getMe();

    loading.dismiss();

    if (!profileInfo.value) {
      console.error("Profile info failed to load.");
      logout("Authentication failure. Please try again.");
      return;
    }
  }

  handleCrossPayeeLogout();
}

async function forgotPassword(username: string) {
  return Auth.forgotPassword(username);
}

async function forgotPasswordSubmit(
  username: string,
  confirmCode: string,
  newPassword: string
) {
  return Auth.forgotPasswordSubmit(username, confirmCode, newPassword);
}

async function logout(customMessage?: string) {
  await Auth.signOut();
  profileInfo.value = undefined;
  router.replace(`/${payee.value ? payee.value.id : "admin"}`);
  const toast = await toastController.create({
    message: customMessage ?? "You have been successfully logged out.",
    duration: 3000,
  });
  toast.present();
}

function createAccount(accountInfo: AccountInfo) {
  return API.post("API", `/v1/payees/${payee.value?.id}/payors`, {
    body: accountInfo,
  });
}

function updateMfa(
  username: string,
  mfa: { phoneNumber: string; enableMfa: boolean }
) {
  return API.put("API", `/v1/users/${username}`, { body: mfa });
}

async function getPhoneNumber(username: string) {
  const loadingSpinner = await loadingController.getTop();
  loadingSpinner.dismiss();

  const modal = await modalController.create({
    component: MfaEnrollmentModal,
    canDismiss: false,
    cssClass: "mfa-modal",
    id: "mfa-modal",
  });
  modal.present();

  const { data } = await modal.onWillDismiss<Ref<string>>();

  if (!data) {
    throw new Error("Phone number not captured");
  }

  await updateMfa(username, { phoneNumber: data.value, enableMfa: true });
}

async function getTextCode(phoneNumber: string): Promise<string> {
  const loadingSpinner = await loadingController.getTop();
  loadingSpinner.dismiss();

  const modal = await modalController.create({
    component: MfaCodeModal,
    componentProps: { phoneNumber },
    canDismiss: false,
    cssClass: "mfa-modal",
    id: "code-modal",
  });
  modal.present();

  const { data } = await modal.onWillDismiss<Ref<string>>();

  if (!data) {
    throw new Error("Phone number not captured");
  }

  return data.value;
}
